import { useCallback, useEffect, useRef } from 'react'

import { logError } from '@/utils/errorHandling'

import { UseAutosaveConfig, UseAutosaveReturn } from '../types'

/**
 * Debounced autosave: calls `onSave` once `delay` ms after the last change.
 * `resetKey` should increment on each local edit so rapid typing collapses into one save.
 *
 * `onSave` / `onError` are kept in refs so parent re-renders do not reset the debounce
 * while `dirty` and `resetKey` are unchanged.
 */
export const useAutosave = ({
	delay,
	enabled,
	dirty,
	onSave,
	onError,
	resetKey = 0,
}: UseAutosaveConfig): UseAutosaveReturn => {
	const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null)
	const onSaveRef = useRef(onSave)
	const onErrorRef = useRef(onError)
	onSaveRef.current = onSave
	onErrorRef.current = onError

	const clearTimers = useCallback(() => {
		if (autosaveTimerRef.current) {
			clearTimeout(autosaveTimerRef.current)
			autosaveTimerRef.current = null
		}
	}, [])

	const cancel = useCallback(() => {
		clearTimers()
	}, [clearTimers])

	const saveNow = useCallback(async () => {
		clearTimers()
		try {
			await onSaveRef.current()
		} catch (e) {
			onErrorRef.current?.(e)
			throw e
		}
	}, [clearTimers])

	useEffect(() => {
		if (!enabled || !dirty || delay <= 0) {
			clearTimers()
			return clearTimers
		}

		clearTimers()
		autosaveTimerRef.current = setTimeout(async () => {
			autosaveTimerRef.current = null
			try {
				// Do not blur the active input — that steals focus while the user is typing
				await onSaveRef.current({ flushActiveElement: false })
			} catch (e) {
				logError(e, { operation: 'useAutosave.debounce' })
				onErrorRef.current?.(e)
			}
		}, delay)

		return clearTimers
	}, [enabled, dirty, delay, resetKey, clearTimers])

	useEffect(() => {
		return clearTimers
	}, [clearTimers])

	return {
		countdown: null,
		cancel,
		saveNow,
	}
}
